export const DEFAULT_FETCH_TIMEOUT_MS = 15_000;
export const UPLOAD_FETCH_TIMEOUT_MS = 120_000;

export type TimedRequestInit = RequestInit & { timeoutMs?: number };

function mergeAbortSignals(signals: Array<AbortSignal | undefined | null>): AbortSignal | undefined {
  const active = signals.filter((s): s is AbortSignal => Boolean(s));
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];
  const controller = new AbortController();
  for (const signal of active) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      return controller.signal;
    }
    signal.addEventListener('abort', () => controller.abort(signal.reason), { once: true });
  }
  return controller.signal;
}

/**
 * fetch with an automatic timeout. Preserves caller-provided AbortSignal (either can abort).
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: TimedRequestInit,
): Promise<Response> {
  const { timeoutMs = DEFAULT_FETCH_TIMEOUT_MS, signal: userSignal, ...rest } = init ?? {};
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => {
    timeoutController.abort(new DOMException('Request timed out', 'TimeoutError'));
  }, timeoutMs);

  const signal = mergeAbortSignals([userSignal, timeoutController.signal]);

  try {
    return await fetch(input, { ...rest, signal });
  } catch (error) {
    if (userSignal?.aborted) {
      throw new Error('Request cancelled');
    }
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timed out after ${Math.round(timeoutMs / 1000)}s`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}
